import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMailAccounts } from "@/hooks/useMailAccounts";
import { useInboxHeadersSessionHydrate } from "@/hooks/useInboxHeadersSessionHydrate";
import { useWarmStandardFolderHeaders } from "@/hooks/useWarmStandardFolderHeaders";
import { useMailInboxSync } from "@/hooks/useMailInboxSync";
import { useMailFolders } from "@/hooks/useMailFolders";
import { useFoldersWithSnapshotUnread } from "@/hooks/useFoldersWithSnapshotUnread";
import { useMailBootstrap } from "@/hooks/useMailBootstrap";
import { useAutoFolderHeadersPrefetch } from "@/hooks/useAutoFolderHeadersPrefetch";
import { usePeriodWarmProgress } from "@/hooks/usePeriodWarmProgress";
import { useInboxAttachmentFlags } from "@/hooks/useInboxAttachmentFlags";
import { isOutboxNavFolder } from "@/lib/outbox/constants";
import { isSnoozedNavFolder } from "@/lib/snooze/constants";
import { buildMobileNavLayoutFromSettings } from "@/lib/mail/folderNavSections";
import { MAIL_LIVE_SYNC_MS } from "@/lib/constants/inbox";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { filterAccountsForMailSync, isGoogleOAuthMailAccount } from "@/lib/accounts/googleOAuthAccounts";
import { isMicrosoftOAuthMailAccount } from "@/lib/accounts/microsoftOAuthAccounts";

/**
 * Données mobile — réutilise le pipeline desktop (cache DD02, bootstrap, sync incrémentale).
 */
type MobileMailOptions = {
  pauseImapWhileCompose?: boolean;
  searchQuery?: string;
  searchInput?: string;
  unreadOnly?: boolean;
  /** false en vue outbox / snooze virtuelle */
  syncEnabled?: boolean;
};

export function useMobileMail(
  selectedUid: number | null = null,
  options: MobileMailOptions = {}
) {
  const { accounts, loading: accountsLoading, error: accountsError, hasAccount, reload: reloadAccounts, accountSettingsVersion, applyAccountOrder, patchAccount, removeAccount } =
    useMailAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [folder, setFolder] = useState("INBOX");

  const active = useMemo(() => {
    if (selectedAccountId != null) {
      return (
        accounts.find((a) => a.id === selectedAccountId) ??
        accounts.find((a) => a.isDefault) ??
        accounts[0]
      );
    }
    return accounts.find((a) => a.isDefault) ?? accounts[0];
  }, [accounts, selectedAccountId]);

  const syncableAccounts = useMemo(
    () => filterAccountsForMailSync(accounts),
    [accounts]
  );

  /** Avant useMailInboxSync — même hydratation render-sync que desktop. */
  useInboxHeadersSessionHydrate(syncableAccounts, { enabled: hasAccount });
  useWarmStandardFolderHeaders(syncableAccounts, { enabled: hasAccount });

  useEffect(() => {
    if (accounts.length === 0) {
      setSelectedAccountId(null);
      return;
    }
    if (selectedAccountId != null && accounts.some((a) => a.id === selectedAccountId)) {
      return;
    }
    const next = accounts.find((a) => a.isDefault) ?? accounts[0];
    setSelectedAccountId(next?.id ?? null);
  }, [accounts, selectedAccountId]);

  const activeImapPaused = active != null && (isGoogleOAuthMailAccount(active) || isMicrosoftOAuthMailAccount(active));

  const imapAccountId = activeImapPaused ? undefined : active?.id;

  const foldersState = useMailFolders(active?.id, { skipImap: activeImapPaused });
  // IMAP brut reste dans foldersState (badgeAhead). Pastilles tiroir = même merge que l’accordéon desktop.
  const foldersWithBadges = useFoldersWithSnapshotUnread(active?.id ?? 0, foldersState.folders);
  const { navFolders, extraNavFolders } = useMemo(() => {
    if (active?.id == null) return { navFolders: [], extraNavFolders: [] };
    const settings = readAccountSettings(active.id);
    const layout = buildMobileNavLayoutFromSettings(foldersWithBadges, {
      folderSlotOverrides: settings.folderSlotOverrides,
      primaryNavOrder: settings.primaryNavOrder,
    });
    return { navFolders: layout.primary, extraNavFolders: layout.others };
  }, [active?.id, accountSettingsVersion, foldersWithBadges]);

  useMailBootstrap(active?.id, foldersState.folders, {
    foldersReady: !foldersState.loading && foldersState.folders.length > 1 && !activeImapPaused,
  });
  useAutoFolderHeadersPrefetch(activeImapPaused ? undefined : active?.id, foldersState.folders);
  usePeriodWarmProgress(activeImapPaused ? undefined : active?.id);

  const isVirtualFolder = isOutboxNavFolder(folder) || isSnoozedNavFolder(folder);
  const inboxAccountId = isVirtualFolder ? undefined : imapAccountId;
  const inboxFolder = isVirtualFolder ? "INBOX" : folder;

  const inbox = useMailInboxSync(
    inboxAccountId,
    inboxFolder,
    options.searchQuery ?? "",
    options.searchInput ?? "",
    options.unreadOnly ?? false,
    {
      folders: foldersState.folders,
      enabled:
        Boolean(active?.id) &&
        !isVirtualFolder &&
        options.syncEnabled !== false &&
        !activeImapPaused,
      pauseImapWhileReading: selectedUid != null || Boolean(options.pauseImapWhileCompose),
    }
  );
  const messages = useInboxAttachmentFlags(active?.id, folder, inbox.messages);

  const reloadFoldersRef = useRef(foldersState.reload);
  reloadFoldersRef.current = foldersState.reload;
  const refreshBadgesRef = useRef(foldersState.refreshBadges);
  refreshBadgesRef.current = foldersState.refreshBadges;
  const reloadInboxRef = useRef(inbox.reload);
  reloadInboxRef.current = inbox.reload;

  const loadingInitial = inbox.loadingInitial;
  const inboxSyncing = inbox.syncing;
  const foldersLoading = foldersState.loading;

  const refresh = useCallback(() => {
    void reloadFoldersRef.current(true);
    void reloadInboxRef.current(true, { background: true, silent: true });
  }, []);

  useEffect(() => {
    if (!active?.id || activeImapPaused) return;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void refreshBadgesRef.current();
    };

    const intervalId = window.setInterval(tick, MAIL_LIVE_SYNC_MS);
    return () => window.clearInterval(intervalId);
  }, [active?.id, activeImapPaused]);

  const selectAccount = useCallback((id: number) => {
    setSelectedAccountId(id);
    setFolder("INBOX");
  }, []);

  const selectFolder = useCallback((name: string) => {
    setFolder(name);
  }, []);

  return {
    accounts,
    accountId: active?.id ?? null,
    currentAccount: active ?? null,
    folder,
    folders: foldersState.folders,
    navFolders,
    extraNavFolders,
    messages,
    total: inbox.total,
    loadingInitial,
    listSettled: inbox.listSettled,
    inboxSyncing,
    fromCache: inbox.fromCache,
    foldersLoading,
    refreshing: inboxSyncing || foldersState.syncing,
    error: inbox.error ?? foldersState.error,
    hasAccount,
    accountsLoading,
    accountsError,
    reloadAccounts,
    applyAccountOrder,
    patchAccount,
    removeAccount,
    accountSettingsVersion,
    refresh,
    syncInBackground: inbox.refreshInBackground,
    markMessageSeen: inbox.markMessageSeen,
    markMessageUnread: inbox.markMessageUnread,
    searchResultQuery: inbox.searchResultQuery,
    markMessageFlagged: inbox.markMessageFlagged,
    setMessagesSeen: inbox.setMessagesSeen,
    removeMessages: inbox.removeMessages,
    adjustFolderUnread: foldersState.adjustFolderUnread,
    reloadInbox: inbox.reload,
    reloadFolders: foldersState.reload,
    refreshBadges: foldersState.refreshBadges,
    hasMore: inbox.hasMore,
    loadingMore: inbox.loadingMore,
    loadMore: inbox.loadMore,
    selectAccount,
    selectFolder,
  };
}
